export const pageTotalHeight = 1056
export const PADDING = 94.4882
export const PAGE_HEIGHT = pageTotalHeight - (2 * PADDING);
const PAGEBREAK_HEIGHT = 40;

// Page-break markers carry the class `custom-pagebreak`. Between paragraphs they are <div>s;
// a paragraph that crosses a page boundary (desktop only) gets an empty, non-editable
// <span class="custom-pagebreak split-pagebreak"> at the start of its first moved line.
// Markers contain no text, so the screenplay text and paragraph structure are untouched;
// (MORE) / CHARACTER (CONT'D) are drawn by CSS from data-label attributes.
const SPLIT_CLASS = 'split-pagebreak';
const SPLITTABLE = ['action', 'dialogue'];

function debounce(func, wait) {
  let timeout;

  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };

    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

const getNodeHeight = (node) => {
  const styles = window.getComputedStyle(node);

  const marginTop = parseFloat(styles.marginTop);
  const marginBottom = parseFloat(styles.marginBottom);
  const paddingTop = parseFloat(styles.paddingTop);
  const paddingBottom = parseFloat(styles.paddingBottom);
  const height = parseFloat(styles.height);

  const totalHeight = height + marginTop + marginBottom + paddingBottom + paddingTop;
  return totalHeight;
}

// Height of a paragraph without any split markers inside it (scene lengths, totals).
export const getUnsplitHeight = (node, measure = getNodeHeight) => {
  let h = measure(node);
  if (node.querySelectorAll) {
    node.querySelectorAll('.' + SPLIT_CLASS).forEach((s) => {
      const st = window.getComputedStyle(s);
      h -= s.getBoundingClientRect().height + parseFloat(st.marginTop) + parseFloat(st.marginBottom);
    });
  }
  return h;
}

export const getTotalHeight = (editor) => {
  const body = editor.getBody()
  let totalHeight = 0
  body.childNodes.forEach(currentNode => {
    if (currentNode && !currentNode.classList.contains('custom-pagebreak')) {
      totalHeight = totalHeight + getUnsplitHeight(currentNode)
    }
  })
  return totalHeight
}

const isSplitMarker = (n) => !!(n && n.nodeType === 1 && n.classList.contains(SPLIT_CLASS));

// ---- selection kept as (paragraph, text offset) so removing/re-adding markers never moves the caret
const pointToBookmark = (body, container, offset) => {
  let block = container;
  while (block && block.parentNode !== body) block = block.parentNode;
  if (!block || block.nodeType !== 1 || block.classList.contains('custom-pagebreak')) return null;
  const doc = body.ownerDocument;
  const r = doc.createRange();
  r.setStart(block, 0);
  r.setEnd(container, offset);
  const afterSplit = container.nodeType === 3
    ? offset === 0 && isSplitMarker(container.previousSibling)
    : isSplitMarker(container.childNodes[offset - 1]);
  return { block, chars: r.toString().length, afterSplit };
};

const bookmarkToPoint = (bm) => {
  if (!bm || !bm.block.isConnected) return null;
  const doc = bm.block.ownerDocument;
  const walker = doc.createTreeWalker(bm.block, 4 /* SHOW_TEXT */);
  let remaining = bm.chars;
  let last = null;
  for (let t = walker.nextNode(); t; t = walker.nextNode()) {
    const len = t.nodeValue.length;
    const next = remaining === len ? walker.nextNode() : null;
    if (remaining === len && next) {
      walker.previousNode();
      // boundary between two text nodes: a marker sits here; keep the caret on its original side
      if (bm.afterSplit && isSplitMarker(next.previousSibling)) return { node: next, offset: 0 };
      return { node: t, offset: len };
    }
    if (remaining <= len) return { node: t, offset: remaining };
    remaining -= len;
    last = t;
  }
  if (last) return { node: last, offset: last.nodeValue.length };
  return bm.chars === 0 ? { node: bm.block, offset: 0 } : null;
};

const removeMarkers = (body) => {
  const parents = new Set();
  body.querySelectorAll('.custom-pagebreak').forEach(node => {
    if (isSplitMarker(node)) parents.add(node.parentNode);
    node.remove();
  });
  parents.forEach(p => p && p.normalize());
};

// ---- finding the first line of a paragraph that no longer fits on the page
const directTextIndex = (node) => {
  const parts = [];
  let total = 0;
  node.childNodes.forEach(c => {
    if (c.nodeType === 3 && c.nodeValue.length) { parts.push({ t: c, start: total }); total += c.nodeValue.length; }
  });
  return { parts, total };
};

// Only paragraphs made of plain text (no nested formatting) are split; others move whole as before.
const isPlainText = (node) => Array.from(node.childNodes).every(c => c.nodeType === 3 || (c.nodeName === 'BR' && c === node.lastChild));

const charRect = (doc, idx, i) => {
  const part = idx.parts.find((p, k) => i >= p.start && (k === idx.parts.length - 1 || i < idx.parts[k + 1].start));
  const r = doc.createRange();
  r.setStart(part.t, i - part.start);
  r.setEnd(part.t, i - part.start + 1);
  const rects = r.getClientRects();
  return rects.length ? rects[rects.length - 1] : r.getBoundingClientRect();
};

// First index whose predicate is true (predicate monotonic false..true); n when none.
const firstIndex = (n, pred) => {
  let lo = 0, hi = n;
  while (lo < hi) { const mid = (lo + hi) >> 1; if (pred(mid)) hi = mid; else lo = mid + 1; }
  return lo;
};

// Returns { part, offset, lineTop } for the first line whose bottom passes limitY, or null when
// all of it fits or not even its first line fits.
const findSplitPoint = (node, fromIndex, limitY) => {
  const doc = node.ownerDocument;
  const idx = directTextIndex(node);
  const n = idx.total;
  if (n - fromIndex < 2) return null;
  const visible = (i) => !/\s/.test(idx.parts.length && charAt(idx, i));
  const bottomAt = (i) => charRect(doc, idx, i).bottom;
  const k = fromIndex + firstIndex(n - fromIndex, (j) => bottomAt(fromIndex + j) > limitY + 0.5);
  if (k >= n) return null;
  const top = charRect(doc, idx, k).top;
  let start = fromIndex + firstIndex(n - fromIndex, (j) => charRect(doc, idx, fromIndex + j).top >= top - 1);
  while (start < n && !visible(start)) start++;
  if (start <= fromIndex || start >= n) return null;
  // at least one visible character must stay above the break
  const firstLineTop = charRect(doc, idx, fromIndex).top;
  if (charRect(doc, idx, start).top <= firstLineTop + 1) return null;
  const part = idx.parts.find((p, i) => start >= p.start && (i === idx.parts.length - 1 || start < idx.parts[i + 1].start));
  return { part: part.t, offset: start - part.start, globalIndex: start, lineTop: charRect(doc, idx, start).top };
};

const charAt = (idx, i) => {
  const part = idx.parts.find((p, k) => i >= p.start && (k === idx.parts.length - 1 || i < idx.parts[k + 1].start));
  return part.t.nodeValue.charAt(i - part.start);
};

const characterCue = (node) => {
  let prev = node.previousElementSibling;
  for (let c = 0; prev && c < 6; c++, prev = prev.previousElementSibling) {
    const cls = prev.classList[0];
    if (cls === 'character') {
      const name = (prev.textContent || '').replace(/\(CONT['’]D\)/gi, '').trim();
      return name ? `${name} (CONT'D)` : null;
    }
    if (cls !== 'dialogue' && cls !== 'parentheses') return null;
  }
  return null;
};

export const autoPaginate = (editor, zoom = 100) => {

  if (!editor) return;
  const body = editor.getBody();
  if (!body) return;
  const doc = body.ownerDocument;
  const win = doc.defaultView;
  const rng = editor.selection.getRng();
  const bmStart = rng ? pointToBookmark(body, rng.startContainer, rng.startOffset) : null;
  const bmEnd = rng && !rng.collapsed ? pointToBookmark(body, rng.endContainer, rng.endOffset) : bmStart;

  // Remove existing page breaks (between and inside paragraphs)
  removeMarkers(body);

  // Splitting runs at every width: the split code measures the live layout (character rects
  // and the probe below), so it adapts to the mobile page geometry without a separate system.
  const splitsEnabled = true;
  const p = editor.dom.create('div', { class: 'custom-pagebreak' });
  body.appendChild(p);
  const probeRect = p.getBoundingClientRect();
  const probe = { left: probeRect.left, width: probeRect.width };
  p.remove();

  const makeBreak = (height) => editor.dom.create('div', {
    class: 'custom-pagebreak',
    contenteditable: 'false',
    style: `margin-top:${pageTotalHeight - (height + PADDING)}px; margin-bottom:${PADDING}px;`,
    'data-page': `${pageNumber + 1}.`
  }, '<div style="page-break-after: always;"></div>');

  // Splits `node` (already placed with `base` px used on its page) at every page boundary it crosses.
  // Returns the height it uses on its final page.
  const splitAcrossPages = (node, base) => {
    const cls = node.classList[0];
    const isDialogue = cls === 'dialogue';
    const lineHeight = parseFloat(win.getComputedStyle(node).lineHeight) || (parseFloat(win.getComputedStyle(node).fontSize) * 1.2);
    const cue = isDialogue ? characterCue(node) : null;
    const reserve = isDialogue ? lineHeight : 0; // room for (MORE)
    const st = win.getComputedStyle(node);
    let segTop = node.getBoundingClientRect().top - parseFloat(st.marginTop);
    let fromIndex = 0;
    let used = base;
    for (let guard = 0; guard < 200; guard++) {
      const limitY = segTop + (PAGE_HEIGHT - used - reserve);
      const point = findSplitPoint(node, fromIndex, limitY);
      if (!point) break;
      const at = used + (point.lineTop - segTop);
      const nRect = node.getBoundingClientRect();
      const contentLeft = nRect.left + parseFloat(st.borderLeftWidth) + parseFloat(st.paddingLeft);
      const contH = cue ? lineHeight : 0;
      const marker = editor.dom.create('span', {
        class: `custom-pagebreak ${SPLIT_CLASS}`,
        contenteditable: 'false',
        'data-page': `${pageNumber + 1}.`,
        style: `display:block; box-sizing:border-box; width:${probe.width}px; margin-left:${probe.left - contentLeft}px; margin-right:0; padding:0; margin-top:${pageTotalHeight - (at + PADDING)}px; margin-bottom:${PADDING + contH}px;`
      }, `<span id="pb-${pageNumber + 1}" style="display:block; page-break-after: always;"></span>`); // id: keeps the editor from discarding this empty marker on save
      if (isDialogue) {
        const more = editor.dom.create('span', { id: `pb-more-${pageNumber + 1}`, class: 'split-cue', 'data-label': '(MORE)', style: `position:absolute; left:${contentLeft - probe.left}px; top:${-(pageTotalHeight - (at + PADDING))}px;` });
        marker.appendChild(more);
        if (cue) {
          const charNode = (() => { let p = node.previousElementSibling; while (p && p.classList[0] !== 'character') p = p.previousElementSibling; return p; })();
          let left = contentLeft - probe.left;
          if (charNode) {
            const cs = win.getComputedStyle(charNode);
            left = charNode.getBoundingClientRect().left + parseFloat(cs.paddingLeft) - probe.left;
          }
          marker.appendChild(editor.dom.create('span', { id: `pb-cont-${pageNumber + 1}`, class: 'split-cue split-cue-character', 'data-label': cue, style: `position:absolute; left:${left}px; top:${PAGEBREAK_HEIGHT + PADDING}px;` }));
        }
      }
      const tail = point.part.splitText(point.offset);
      node.insertBefore(marker, tail);
      pageNumber++;
      const mRect = marker.getBoundingClientRect();
      segTop = mRect.bottom + PADDING;
      used = contH;
      fromIndex = point.globalIndex;
    }
    const nRect = node.getBoundingClientRect();
    return used + (nRect.bottom + parseFloat(st.marginBottom) - segTop);
  };

  const canSplit = (node) => splitsEnabled && node.nodeType === 1 && SPLITTABLE.includes(node.classList[0]) && isPlainText(node);

  // Get updated list of content nodes
  const contentNodes = Array.from(body.childNodes);
  let currentPageHeight = 0;
  let pageNumber = 1;

  contentNodes.forEach((node, index) => {
    if (node.classList && node.classList.contains('custom-pagebreak')) {
      return;
    }

    const nodeHeight = getNodeHeight(node);
    if (currentPageHeight + nodeHeight > PAGE_HEIGHT) {
      // Desktop: let long action / dialogue continue on the next page instead of moving whole.
      if (canSplit(node)) {
        const before = pageNumber;
        const h = splitAcrossPages(node, currentPageHeight);
        if (pageNumber > before) { currentPageHeight = h; return; }
      }
      const prev = node.previousElementSibling;
      if ((node.classList[0] === 'dialogue' || node.classList[0] === 'parentheses') && prev &&
      (prev.classList[0]==='character' || prev.previousElementSibling?.classList[0]==='character' )) {
        let previousNode = prev
        let newHeight = getNodeHeight(previousNode)
        let count = 0
        while (previousNode.classList[0] !== 'character' && count < 3) {
          previousNode = previousNode.previousElementSibling;
          newHeight += getNodeHeight(previousNode)
          count++
        }

        currentPageHeight -= newHeight
        body.insertBefore(makeBreak(currentPageHeight), previousNode);
        pageNumber++;
        currentPageHeight = newHeight + nodeHeight;
      }
      else if (node.classList[0] === 'action' && prev?.classList[0] === 'sceneHeadings') {
        let newHeight = getNodeHeight(prev)
        currentPageHeight -= newHeight
        body.insertBefore(makeBreak(currentPageHeight), prev);
        pageNumber++;
        currentPageHeight = newHeight + nodeHeight;
      }
      else {
        body.insertBefore(makeBreak(currentPageHeight), node);
        pageNumber++;
        currentPageHeight = nodeHeight;
      }
      // A block moved to a new page that is itself longer than the space left there.
      if (currentPageHeight > PAGE_HEIGHT && canSplit(node)) {
        currentPageHeight = splitAcrossPages(node, currentPageHeight - nodeHeight);
      }
    } else {
      currentPageHeight += nodeHeight;
    }
  });

  // Restore selection
  const s = bookmarkToPoint(bmStart);
  const e = bmEnd === bmStart ? s : bookmarkToPoint(bmEnd);
  if (s && e) {
    const r = doc.createRange();
    r.setStart(s.node, s.offset);
    r.setEnd(e.node, e.offset);
    editor.selection.setRng(r);
  } else if (rng) {
    try { editor.selection.setRng(rng); } catch (err) { /* selection no longer valid */ }
  }

  // Set page height
  const numberOfPages = pageNumber;
  const newPageHeight = (pageTotalHeight * numberOfPages) + (PAGEBREAK_HEIGHT * (numberOfPages - 1)) - PADDING;
  editor.getBody().style.height = `${newPageHeight}px`;
};

export const debounceAutoPaginate = debounce(autoPaginate, 100);

// Keeps Backspace / Delete / Left / Right working across a page boundary inside a paragraph:
// the caret steps over the marker instead of selecting or deleting it.
export const handleSplitMarkerKeys = (editor, e) => {
  if (!['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight'].includes(e.key)) return;
  if (e.shiftKey || e.ctrlKey || e.metaKey || e.altKey) return;
  const rng = editor.selection.getRng();
  if (!rng || !rng.collapsed) return;
  const { startContainer: c, startOffset: o } = rng;
  const textLen = (n) => (n.nodeValue || '').replace(/\ufeff/g, '').length;
  const atStart = c.nodeType === 3 && (o === 0 || c.nodeValue.slice(0, o).replace(/\ufeff/g, '') === '');
  const atEnd = c.nodeType === 3 && c.nodeValue.slice(o).replace(/\ufeff/g, '') === '';
  const before = c.nodeType === 3 ? (atStart ? c.previousSibling : null) : c.childNodes[o - 1];
  const after = c.nodeType === 3 ? (atEnd ? c.nextSibling : null) : c.childNodes[o];
  const backwards = e.key === 'Backspace' || e.key === 'ArrowLeft';
  let marker = backwards ? before : after;
  // Caret on the other side of the marker: Backspace at the end of the line above it, or Delete at
  // the start of the line below it, would otherwise hit the hidden line-end space.
  if (!isSplitMarker(marker) && e.key === 'Backspace' && isSplitMarker(after) && !atStart) {
    e.preventDefault();
    editor.undoManager.transact(() => {
      const v = c.nodeValue.slice(0, o).replace(/\ufeff/g, '');
      const head = v.slice(0, -1);
      c.nodeValue = head + c.nodeValue.slice(o).replace(/\ufeff/g, '');
      const r = editor.getDoc().createRange(); r.setStart(c, head.length); r.collapse(true); editor.selection.setRng(r);
    });
    editor.nodeChanged();
    return;
  }
  if (!isSplitMarker(marker)) return;
  const doc = editor.getDoc();
  const prevText = marker.previousSibling && marker.previousSibling.nodeType === 3 ? marker.previousSibling : null;
  const nextText = marker.nextSibling && marker.nextSibling.nodeType === 3 ? marker.nextSibling : null;
  const place = (node, offset) => { const r = doc.createRange(); r.setStart(node, offset); r.collapse(true); editor.selection.setRng(r); };
  if (e.key === 'ArrowLeft') { e.preventDefault(); if (prevText) place(prevText, prevText.nodeValue.length); return; }
  if (e.key === 'ArrowRight') { e.preventDefault(); if (nextText) place(nextText, 0); return; }
  // Delete one character on the other side of the page boundary; the marker itself is never removed.
  const target = backwards ? prevText : nextText;
  if (!target || !textLen(target)) return;
  e.preventDefault();
  editor.undoManager.transact(() => {
    const v = target.nodeValue;
    let k = backwards ? v.length - 1 : 0;
    while (v.charAt(k) === '\ufeff' && k >= 0 && k < v.length) k += backwards ? -1 : 1;
    target.deleteData(k, 1);
    if (backwards) { if (nextText) place(nextText, 0); else place(target, target.nodeValue.length); }
    else place(target, 0);
  });
  editor.nodeChanged();
};

export const onPageBreakSelect = (editor) => {
  const node = editor.selection.getNode();
  const range = editor.selection.getRng();

  const nextNode = node.nextSibling
  if (nextNode) {
    range.setStartBefore(nextNode);
    range.collapse(true);
    editor.selection.setRng(range);
  }
}
